/* eslint-disable react/destructuring-assignment */
import { DataSource } from 'hooks/useHttpClient';
import { BasicMappedStatus, Resume } from './Candidate';
import { Comment, CommentListResolver } from './Comment';
import { FactAudit, PaginatedList, PaginationMeta, PaginationMetaResolver } from './Common';
import { Company, QuickCompanySingleResolver } from './Company';
import { Job, JobSingleResolver } from './Job';
import { Member, MemberListFromJobResolver } from './Members';
import { Task, TaskSingleResolver } from './Task';

export type OnboardingStatus = BasicMappedStatus & { color: string };

export const OnboardingStatuses: OnboardingStatus[] = [
    { id: 1, name: 'Completed', color: 'blue' },
    { id: 2, name: 'On Hold', color: 'gray' },
    { id: 3, name: 'On Time', color: 'green' },
    { id: 4, name: 'Possible Setback', color: 'yellow' },
    { id: 5, name: 'Delayed', color: 'red' }
];

export const OnboardingStages: BasicMappedStatus[] = [
    { id: 1, name: 'Requirements' },
    { id: 2, name: 'Implementation' },
    { id: 3, name: 'Delivery' }
];

export const QuestionnaireStages: BasicMappedStatus[] = [
    { id: 1, name: 'General Information' },
    { id: 2, name: 'Meetings' },
    { id: 3, name: 'Before Day 1' },
    { id: 4, name: 'Day 1 expectations' }
];

export type FactOnboardingQuestion = {
    id: number;
    questionId: number;
    question: string;
    answer: string;
    description: string;
    answeredBy?: FactAudit;
    feedback: {
        id: number;
        feedback: string;
        createdBy: FactAudit;
    }[];
    order: number;
    stage: BasicMappedStatus;
};

export type OnboardingCandidate = {
    id: number;
    applicationId?: number;
    startDate?: string;
    email: string;
    fullName: string;
    phoneNumber: string;
    location: string;
    avatar: string;
    resume: Resume;
    createdBy: FactAudit;
};

export type Onboarding = {
    id: string;
    candidate: OnboardingCandidate | null;
    candidatesAvailable: OnboardingCandidate[];
    comments: Comment[];
    job: Job;
    company: Company;
    updatedBy: FactAudit;
    createdBy: FactAudit;
    members: Member[];
    status: OnboardingStatus;
    stage: BasicMappedStatus;
};

export const NastyQuestionnaireResolver = (dataSource: DataSource, apiResponse: any): FactOnboardingQuestion[] => {
    try {
        switch (dataSource) {
            case 'hyrerocket': {
                const questions: FactOnboardingQuestion[] = [];
                Object.keys(apiResponse).forEach((key) => {
                    const questionsList = apiResponse[key];
                    questionsList.questions.forEach((question: any) => {
                        questions.push({
                            id: question.answer.id,
                            questionId: question.id,
                            question: question.question,
                            answer: question.answer.answer,
                            description: question.description,
                            answeredBy: question.answer.answer
                                ? {
                                      userId: question.answer.user?.id,
                                      userFullName: question.answer.user?.name,
                                      avatar: '',
                                      dateStr: question.answer.updated_at || ''
                                  }
                                : undefined,
                            feedback: question.feedback.map((feedback: any) => ({
                                id: feedback.id,
                                feedback: feedback.comment,
                                createdBy: {
                                    userId: feedback.user?.id,
                                    userFullName: feedback.user?.name,
                                    avatar: '',
                                    dateStr: feedback.created_at
                                }
                            })),
                            order: question.sequence,
                            stage:
                                QuestionnaireStages.find((status) => status.id === +String(question.stage).slice(-1)) ||
                                QuestionnaireStages[0]
                        });
                    });
                });
                return questions;
            }

            default:
                return [];
        }
    } catch (error) {
        console.log('Nasty Questionnaire Here', error);
        return [];
    }
};

export const OnboardingCandidateSingleResolver = (
    dataSource: DataSource,
    apiResponse: any,
    applicationApiResponse?: any
): OnboardingCandidate | null => {
    if (!apiResponse) {
        return null;
    }
    try {
        switch (dataSource) {
            case 'hyrerocket':
                return {
                    id: apiResponse.id,
                    applicationId: applicationApiResponse?.id,
                    startDate: applicationApiResponse?.start_date,
                    email: apiResponse.email,
                    fullName: `${apiResponse.first_name} ${apiResponse.last_name}`,
                    phoneNumber: apiResponse.phone_number,
                    location: apiResponse.location,
                    avatar: apiResponse.avatar,
                    resume: {
                        url: apiResponse.resume_url,
                        source: apiResponse.resume_source
                    },
                    createdBy: {
                        userId: apiResponse.created_by?.id,
                        userFullName: apiResponse.created_by?.name,
                        avatar: apiResponse.created_by?.avatar,
                        dateStr: apiResponse.created_by?.created_at
                    }
                };

            default:
                return null;
        }
    } catch (error) {
        console.log('Single Onboarding Candidate Here', error);
        return null;
    }
};

export const OnboardingSingleResolver = (dataSource: DataSource, apiResponse: any): Onboarding | null => {
    try {
        switch (dataSource) {
            case 'hyrerocket':
                return {
                    id: apiResponse.id,
                    candidate: OnboardingCandidateSingleResolver(dataSource, apiResponse.candidate, apiResponse.job_requisition_candidate),
                    candidatesAvailable: apiResponse.candidates_available
                        ? apiResponse.candidates_available.map((candidate: any) => OnboardingCandidateSingleResolver(dataSource, candidate))
                        : [],
                    comments: CommentListResolver(dataSource, apiResponse.comments, true),
                    job: JobSingleResolver(dataSource, apiResponse.job_requisition, false) as Job,
                    company: QuickCompanySingleResolver(dataSource, apiResponse.company) as Company,
                    updatedBy: {
                        userId: apiResponse.updated_by?.id || 0,
                        userFullName: apiResponse.updated_by?.name || 'System',
                        avatar: apiResponse.updated_by?.avatar || undefined,
                        dateStr: apiResponse.updated_at
                    },
                    createdBy: {
                        userId: 0,
                        userFullName: 'System',
                        dateStr: apiResponse.created_at
                    },
                    members: MemberListFromJobResolver(dataSource, apiResponse.members, true),
                    status:
                        OnboardingStatuses.find((status) => status.color === String(apiResponse.status).toLowerCase()) ||
                        OnboardingStatuses[4],
                    stage: OnboardingStages.find((status) => status.id === apiResponse.stage.id) || OnboardingStages[0]
                };

            default:
                return null;
        }
    } catch (error) {
        return null;
    }
};

export const OnboardingListResolver = (dataSource: DataSource, apiResponse: any): PaginatedList<Onboarding> | null => {
    try {
        switch (dataSource) {
            case 'hyrerocket':
                return {
                    data: apiResponse.data.map((onboarding: any) => OnboardingSingleResolver(dataSource, onboarding)),
                    meta: PaginationMetaResolver(dataSource, apiResponse.meta) as PaginationMeta
                };

            default:
                return null;
        }
    } catch (error) {
        return null;
    }
};

export const OnboardingTaskListResolver = (dataSource: DataSource, apiResponse: any): Task[] => {
    try {
        switch (dataSource) {
            case 'hyrerocket':
                return apiResponse.map((task: any) => TaskSingleResolver(dataSource, task)).filter((task: any) => task !== null);

            default:
                return [];
        }
    } catch (error) {
        return [];
    }
};
